/* =========================================================
   STACK VARIANTS — FORCED dependency (SAFE)
   - No MutationObserver (prevents infinite re-render loops)
   - Throttled with requestAnimationFrame
   - Form fieldset:          data-fieldset-index="0"
   - Configuration fieldset: data-fieldset-index="1"

   Rules:
     Regular  => show all configs EXCEPT "Quadruple Column / Double Tier"
     Quadrant => show ONLY:
                   "Single Column / Double Tier"
                   "Quadruple Column / Double Tier"
   ========================================================= */

(() => {
  const HANDLE = "stack";

  const norm = (s) => (s ?? "").toString().trim().replace(/\s+/g, " ");

  const QUADRANT_ALLOWED = new Set([
    "Single Column / Double Tier",
    "Quadruple Column / Double Tier",
  ]);

  const REGULAR_BLOCKED = new Set([
    "Quadruple Column / Double Tier",
  ]);

  function getPicker() {
    return (
      document.querySelector(`variant-picker[data-product-url="/products/${HANDLE}"][ref="mainVariantPicker"]`) ||
      document.querySelector(`variant-picker[data-product-url="/products/${HANDLE}"]`) ||
      document.querySelector("variant-picker")
    );
  }

  function getFieldset(root, idx) {
    return root?.querySelector(`fieldset[data-fieldset-index="${idx}"]`) || null;
  }

  function getCheckedValue(fs) {
    const el = fs?.querySelector('input[type="radio"]:checked');
    return el ? norm(el.value) : "";
  }

  function getInputs(fs) {
    return Array.from(fs?.querySelectorAll('label.variant-option__button-label > input[type="radio"]') || []);
  }

  function setVisible(input, visible) {
  const label = input.closest("label.variant-option__button-label");
  if (!label) return;

  if (visible) {
    label.hidden = false;
    label.style.removeProperty("display");
    label.style.removeProperty("pointer-events");
    label.style.removeProperty("visibility");
    label.style.removeProperty("position");
    label.style.removeProperty("left");
  } else {
    // triple-lock hide so Horizon can't "bring it back"
    label.hidden = true;
    label.style.setProperty("display", "none", "important");
    label.style.setProperty("pointer-events", "none", "important");

    // extra safety in case something forces display back
    label.style.setProperty("visibility", "hidden", "important");
    label.style.setProperty("position", "absolute", "important");
    label.style.setProperty("left", "-99999px", "important");
  }
}


  function setDisabled(input, disabled) {
    input.disabled = disabled;
    if (disabled) input.setAttribute("aria-disabled", "true");
    else input.removeAttribute("aria-disabled");
  }

  function clickRadio(input) {
    if (!input || input.disabled) return;
    input.checked = true;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  function apply(root) {
    const fsForm = getFieldset(root, 0);
    const fsCfg  = getFieldset(root, 1);
    if (!fsForm || !fsCfg) return;

    const form = getCheckedValue(fsForm);
    if (!form) return;

    const cfgInputs = getInputs(fsCfg);
    if (!cfgInputs.length) return;

    // Decide allowed set by forced rules
    let allowed;
    if (form === "Quadrant") {
      allowed = QUADRANT_ALLOWED;
    } else {
      // Regular (default)
      allowed = new Set(cfgInputs.map(i => norm(i.value)));
      for (const b of REGULAR_BLOCKED) allowed.delete(b);
    }

    // Apply visibility & disabled
    for (const input of cfgInputs) {
      const v = norm(input.value);
      const ok = allowed.has(v);
      setDisabled(input, !ok);
      setVisible(input, ok);
    }

    // Auto-fix selection if invalid
    const checked = fsCfg.querySelector('input[type="radio"]:checked');
    if (!checked || checked.disabled) {
      const first = cfgInputs.find(i => !i.disabled);
      if (first) clickRadio(first);
    }
  }

  function init() {
  if (!location.pathname.includes(`/products/${HANDLE}`)) return;

  let applying = false;
  let scheduled = false;

  const run = () => {
    if (scheduled) return;
    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;

      const root = getPicker();
      if (!root) return;

      if (applying) return;
      applying = true;
      try {
        apply(root);
      } finally {
        applying = false;
      }

      // Horizon second-pass render
      setTimeout(() => {
        const r = getPicker();
        if (r) apply(r);
      }, 60);
    });
  };

  // initial
  run();

  // user interaction
  document.addEventListener("change", (e) => {
    if (e.target && e.target.matches('input[type="radio"]')) run();
  });

  document.addEventListener("input", (e) => {
    if (e.target && e.target.matches('input[type="radio"]')) run();
  });

  // Horizon URL updates (?variant=)
  window.addEventListener("popstate", run);

  // FINAL SAFETY: Horizon replaces picker node entirely
  const bodyObserver = new MutationObserver(() => run());
  bodyObserver.observe(document.body, { childList: true, subtree: true });
}

  document.addEventListener("DOMContentLoaded", init);
})();
